import React, {createContext, useState, useEffect, useMemo} from 'react';
import {useACFOptions} from "../../_hooks/useACFOptions";

export const LocationContext = createContext({
  location: {
    countryCode: null,
    country: null,
    timezone: null
  },
  region: 'global',
  isInEU: false,
  isInUSA: false,
  isGlobal: true,
  loading: true
});

export const LocationProvider = ({children}) => {
  const [location, setLocation] = useState({
    countryCode: null,
    country: null,
    timezone: null
  });

  const [loading, setLoading] = useState(true);

  const {quizSettings} = useACFOptions();

  // An empty region field in the options must not take the whole site down:
  // this provider wraps every page.
  const parseCountryCodes = (value) => (value || '')
    .split('/')
    .map(code => code.trim().toUpperCase())
    .filter(Boolean);

  const EUCountryCodes = useMemo(() => {
    return parseCountryCodes(quizSettings?.euRegion);
  }, [quizSettings]);

  const USACountryCodes = useMemo(() => {
    return parseCountryCodes(quizSettings?.usaRegion);
  }, [quizSettings]);

  const key = process.env.GATSBY_IP_API_KEY || '';

  // EU and USA are explicit allow lists. Everything else is global, including a
  // country we could not resolve at all — we only narrow the content down for
  // visitors we can actually place.
  const region = useMemo(() => {
    const code = location.countryCode;

    if (!code) {
      return 'global';
    }

    if (EUCountryCodes.includes(code)) {
      return 'eu';
    }

    if (USACountryCodes.includes(code)) {
      return 'usa';
    }

    return 'global';
  }, [location.countryCode, EUCountryCodes, USACountryCodes]);

  const isInEU = region === 'eu';
  const isInUSA = region === 'usa';
  const isGlobal = region === 'global';

  useEffect(() => {
    // Hides the giveaway links in the navigation. Held back until the lookup
    // settles so the links do not blink for EU and USA visitors.
    document.documentElement.classList.toggle('region-other', !loading && isGlobal);
  }, [loading, isGlobal]);

  useEffect(() => {
    const storedCountryCode = sessionStorage.getItem('countryCode') || null;
    const storedCountry = sessionStorage.getItem('country') || null;
    const storedTimezone = sessionStorage.getItem('timezone') || null;

    if (storedCountryCode && storedTimezone) {
      setLocation({
        countryCode: storedCountryCode,
        country : storedCountry,
        timezone: storedTimezone
      });
      setLoading(false);
    } else {
      const getLocationInfo = async () => {
        try {
          const apiUrl = key ? `https://pro.ip-api.com/json/?key=${key}` : '/ip-api/';

          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Country code request failed');
          }

          const result = await response.json();

          const countryCode = result.countryCode.toUpperCase();
          const country = result.country;
          const timeZone = result.timezone;

          setLocation({
            countryCode: countryCode,
            country : country,
            timezone: timeZone
          });
          sessionStorage.setItem('countryCode', countryCode);
          sessionStorage.setItem('country', country);
          sessionStorage.setItem('timezone', timeZone);

          setLoading(false);
        } catch (error) {
          console.error('Country code request failed: ', error.message);
          setLoading(false);
        }
      };

      getLocationInfo();
    }
  }, []);

  return (
    <LocationContext.Provider value={{location, region, isInEU, isInUSA, isGlobal, loading}}>
      {children}
    </LocationContext.Provider>
  );
};
